import { defineBoot } from '#q-app'
import type { AxiosInstance } from 'axios'
import axios from 'axios'
import { useAccountStore } from '@/stores/account'

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance
  }
}

declare module 'axios' {
  interface AxiosRequestConfig {
    cancelPreviousRequest?: boolean
  }
}

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({
  baseURL: '/api/internal/ops',
  xsrfCookieName: 'csrftoken2',
  xsrfHeaderName: 'X-CSRFToken',
  withXSRFToken: true,
  withCredentials: true,
})

const pendingRequests = {} as Record<string, AbortController>

api.interceptors.request.use(
  (config) => {
    if (config.cancelPreviousRequest && config?.url && !config.signal) {
      const path = config.url?.split('?')[0]
      const requestKey = `${config.method}:${path}`
      pendingRequests[requestKey]?.abort()
      const controller = new AbortController()
      config.signal = controller.signal
      pendingRequests[requestKey] = controller
    }
    return config
  },
  (error) => {
    console.log('interceptor request error', error)
    return Promise.reject(error)
  },
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      await useAccountStore().logout()
    }

    return Promise.reject(error)
  },
)

export default defineBoot(({ app }) => {
  // for use inside Vue files (Options API) through this.$axios and this.$api

  app.config.globalProperties.$axios = axios
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API
})

export { api }
